/**
 * Markdown Parser for Support Articles
 * Uses professional markdown libraries: remark, remark-html
 * Custom YAML frontmatter parser for browser compatibility
 */

import { remark } from "remark";
import remarkHtml from "remark-html";

// Simple YAML frontmatter parser (browser-compatible)
const parseFrontmatter = (content) => {
  const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    throw new Error("Invalid markdown file: missing frontmatter");
  }

  const frontmatterText = match[1];
  const markdownContent = match[2];

  // Simple YAML parser for our use case
  const frontmatter = {};
  const lines = frontmatterText.split("\n");
  let currentKey = null;
  let currentValue = [];
  let inArray = false;
  let inObject = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) continue;

    // Check for key-value pairs
    if (
      trimmed.includes(":") &&
      !trimmed.startsWith("-") &&
      !inArray &&
      !inObject
    ) {
      // Flush previous key
      if (currentKey) {
        if (currentValue.length === 1) {
          frontmatter[currentKey] = currentValue[0];
        } else {
          frontmatter[currentKey] = currentValue;
        }
      }

      const colonIndex = trimmed.indexOf(":");
      currentKey = trimmed.substring(0, colonIndex).trim();
      const value = trimmed.substring(colonIndex + 1).trim();

      if (value) {
        currentValue = [value];
      } else {
        currentValue = [];
        inArray = true;
      }
    }
    // Check for array items
    else if (trimmed.startsWith("- ") && inArray) {
      currentValue.push(trimmed.substring(2).trim());
    }
    // Check for object properties
    else if (trimmed.startsWith("  ") && inObject) {
      const colonIndex = trimmed.indexOf(":");
      if (colonIndex > 0) {
        const subKey = trimmed.substring(0, colonIndex).trim();
        const subValue = trimmed.substring(colonIndex + 1).trim();
        if (!frontmatter[currentKey]) {
          frontmatter[currentKey] = {};
        }
        frontmatter[currentKey][subKey] = subValue;
      }
    }
    // Start of object
    else if (trimmed.endsWith(":") && !trimmed.startsWith("-")) {
      inObject = true;
      inArray = false;
    }
    // End of array/object
    else if (!trimmed.startsWith("  ") && !trimmed.startsWith("-")) {
      inArray = false;
      inObject = false;
    }
  }

  // Flush last key
  if (currentKey) {
    if (currentValue.length === 1) {
      frontmatter[currentKey] = currentValue[0];
    } else {
      frontmatter[currentKey] = currentValue;
    }
  }

  return { frontmatter, content: markdownContent };
};

// Convert markdown to HTML using remark
const markdownToHtml = async (markdown) => {
  const result = await remark()
    .use(remarkHtml, {
      // Add custom CSS classes for styling
      sanitize: false, // Allow HTML in markdown
    })
    .process(markdown);

  return result.toString();
};

// Parse a markdown file (async version)
export const parseMarkdownFile = async (fileContent) => {
  try {
    const { frontmatter, content } = parseFrontmatter(fileContent);
    const htmlContent = await markdownToHtml(content);

    return {
      ...frontmatter,
      content: {
        html: htmlContent,
        markdown: content,
      },
    };
  } catch (error) {
    console.error("Error parsing markdown file:", error);
    throw error;
  }
};

// Parse a markdown file (sync version for backward compatibility)
export const parseMarkdownFileSync = (fileContent) => {
  try {
    const { frontmatter, content } = parseFrontmatter(fileContent);

    // For sync version, we'll return the markdown content as-is
    // and let the component handle the HTML conversion
    return {
      ...frontmatter,
      content: {
        html: content, // Will be processed by remark in the component
        markdown: content,
      },
    };
  } catch (error) {
    console.error("Error parsing markdown file:", error);
    throw error;
  }
};

// Load and parse a markdown file
export const loadMarkdownFile = async (filePath) => {
  try {
    const response = await fetch(filePath);
    const content = await response.text();
    return parseMarkdownFile(content);
  } catch (error) {
    console.error(`Error loading markdown file ${filePath}:`, error);
    throw error;
  }
};

export default {
  parseMarkdownFile,
  parseMarkdownFileSync,
  loadMarkdownFile,
  markdownToHtml,
};
